import type { NextFn } from '@warp-drive/core/request';
import type { RequestContext } from '@warp-drive/core/types/request';
import { authContext } from '../auth-context.ts';
import { emitRequestEvent } from '../request-events.ts';

const DEFAULT_TOAST_PREFIX: Record<number, string> = {
  400: 'Bad request: ',
  403: 'Access denied: ',
};

const FALLBACK_ERROR_MESSAGE =
  'An error occurred. Please try again or contact support if the problem persists.';

/**
 * STATIC policy only (ADR-001): which statuses are session-fatal and which
 * surface a user-facing message. Outcomes are EMITTED on
 * `tio-data/request-events` — the app shell subscribes and maps them to
 * session.invalidate / toasts / Datadog. The handler never calls UI or
 * session APIs, so it is constructible at module scope.
 */
export interface ResponseHandlerOptions {
  /** Statuses that emit `unauthorized` (session-fatal). Default: [401]. */
  invalidateStatuses?: number[];
  /** Statuses that emit `request-failed` with an extracted message. */
  toastStatuses?: number[];
}

interface RequestError extends Error {
  status?: number;
  statusText?: string;
  isRequestError?: boolean;
  content?: unknown;
}

function extractErrorMessage(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return FALLBACK_ERROR_MESSAGE;
  const p = payload as Record<string, unknown>;

  if (Array.isArray(p.errors) && p.errors.length > 0) {
    const first = p.errors[0] as Record<string, unknown>;
    return (first.detail as string) || (first.title as string) || FALLBACK_ERROR_MESSAGE;
  }
  if (typeof p.error === 'string') return p.error;
  if (typeof p.message === 'string') return p.message;
  return FALLBACK_ERROR_MESSAGE;
}

/**
 * Catches errors thrown by downstream handlers (typically Fetch on non-2xx)
 * and mirrors `ApplicationAdapter#ensureResponseAuthorized`: emits
 * `unauthorized` for session-fatal statuses and `request-failed` (with an
 * extracted, prefixed message) for configured 4xx statuses. The original
 * error is always re-thrown so callers can still `try/catch` it.
 *
 * Skipped entirely while `authContext.isAuthenticated()` is false —
 * pre-login flows (forgot-password, enroll) own their own error handling
 * and shouldn't be force-logged-out by a stale 401.
 */
export class TioResponseHandler {
  private invalidateStatuses: Set<number>;
  private toastStatuses: Set<number>;

  constructor(options: ResponseHandlerOptions = {}) {
    this.invalidateStatuses = new Set(options.invalidateStatuses ?? [401]);
    this.toastStatuses = new Set(options.toastStatuses ?? []);
  }

  async request<T>(context: RequestContext, next: NextFn<T>) {
    try {
      return await next(context.request);
    } catch (raw) {
      const error = raw as RequestError;
      const status = error?.status;

      if (typeof status === 'number' && error.isRequestError && authContext.isAuthenticated()) {
        this.handleStatus(status, error, context);
      }

      throw raw;
    }
  }

  private handleStatus(status: number, error: RequestError, context: RequestContext) {
    const userId = authContext.userId();
    const base = {
      status,
      payload: error.content,
      url: context.request.url,
      method: context.request.method ?? 'GET',
      userId,
    };

    if (this.invalidateStatuses.has(status)) {
      console.error('TioResponseHandler caught unauthorized response', base);
      emitRequestEvent({ type: 'unauthorized', ...base });
      return;
    }

    if (this.toastStatuses.has(status)) {
      const raw = extractErrorMessage(error.content);
      const message = `${DEFAULT_TOAST_PREFIX[status] ?? ''}${raw}`;

      console.warn('TioResponseHandler caught non-fatal error', base);
      emitRequestEvent({ type: 'request-failed', message, ...base });
    }
  }
}
